import React, { useMemo, useEffect } from 'react';
import { Modal, Button, Table, Tooltip, Typography, Space, Alert } from 'antd';
import { CheckCircleOutlined, AmazonOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

const ConfirmShipmentModal = ({ visible, onCancel, onConfirm, shipmentReadyOrders, loading }) => {
    // Add CSS for error rows
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .confirm-shipment-modal .error-row {
                background-color: #fff2f0 !important;
            }
            .confirm-shipment-modal .error-row:hover {
                background-color: #ffe7e6 !important;
            }
            .confirm-shipment-modal .error-row td {
                border-bottom: 1px solid #ffccc7;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Check if an order needs last mile data
    const needsLastMile = (carrier) => {
        return carrier && (carrier.toLowerCase() === 'yunexpress' || carrier.toLowerCase() === 'yanwen');
    };

    // Validate orders and categorize them
    const { validOrders, invalidOrders } = useMemo(() => {
        const valid = [];
        const invalid = [];

        shipmentReadyOrders.forEach(order => {
            let hasError = false;
            let errorReasons = [];

            // Remove fulfillment status check - allow any status
            // Removed: Check fulfillment status requirement

            // Check last mile data for specific carriers
            if (needsLastMile(order.carrier) && !order.lastMileTrackingNumber) {
                hasError = true;
                errorReasons.push(`Missing last mile tracking number for ${order.carrier}`);
            }

            if (hasError) {
                invalid.push({
                    ...order,
                    errorReason: errorReasons.join('; ')
                });
            } else {
                valid.push(order);
            }
        });

        return { validOrders: valid, invalidOrders: invalid };
    }, [shipmentReadyOrders]);

    const allOrders = [...validOrders, ...invalidOrders];
    const hasInvalidOrders = invalidOrders.length > 0;

    const columns = [
        {
            title: '#',
            key: 'index',
            width: 50,
            align: 'center',
            render: (_, record, index) => (
                <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{index + 1}</Text>
            )
        },
        {
            title: 'Order ID',
            dataIndex: 'amazonOrderId',
            key: 'amazonOrderId',
            width: 190,
            render: (text) => (
                <Space>
                    <AmazonOutlined style={{ color: '#FF9900', fontSize: 12 }} />
                    <Text style={{ fontSize: 12 }}>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Order Item ID',
            dataIndex: 'orderItemId',
            key: 'orderItemId',
            width: 160,
            render: (text) => <Text style={{ fontSize: 12 }}>{text}</Text>
        },
        {
            title: 'SKU / Product Name',
            dataIndex: 'sellerSKU',
            key: 'sellerSKU',
            ellipsis: true,
            render: (text, record) =>
                <span>
                    <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{text}</Text><br />
                    <Tooltip title={text} placement='topLeft'>
                        <Text>{record.title}</Text>
                    </Tooltip>
                </span>
        },
        {
            title: 'Customer',
            dataIndex: 'customerName',
            key: 'customerName',
            width: 140,
            render: (text) => <Text style={{ fontSize: 12 }}>{text}</Text>
        },
        {
            title: 'Tracking / Carrier',
            dataIndex: 'trackingNumber',
            key: 'trackingCarrier',
            width: 200,
            ellipsis: true,
            render: (text, record) => {
                return (
                    <span>
                        <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{text || 'N/A'}</Text><br />
                        <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{record.carrier || 'N/A'}</Text>
                    </span>
                );
            }
        },
        {
            title: 'Last Mile',
            key: 'lastMile',
            width: 200,
            render: (_, record) => {
                if (needsLastMile(record.carrier)) {
                    if (record.errorReason) {
                        return (
                            <Text type="danger" style={{ fontSize: 11 }}>
                                <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                                {record.errorReason}
                            </Text>
                        );
                    } else {
                        return (
                            <div>
                                <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>{record.lastMileTrackingNumber || 'N/A'}</Text><br />
                                <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{record.lastMileCarrier || 'N/A'}</Text>
                            </div>
                        );
                    }
                } else {
                    return <Text style={{ fontSize: 11, color: '#999' }}>Not required</Text>;
                }
            }
        },
        {
            title: 'Fulfillment',
            dataIndex: 'fulfillmentStatus',
            key: 'fulfillmentStatus',
            width: 120,
            render: (text) => {
                // Show status without color coding since all statuses are now valid
                return (
                    <Text style={{ fontSize: 12 }}>
                        {text || 'N/A'}
                    </Text>
                );
            }
        },
    ];

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span>Confirm Shipment</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    color='green'
                    variant="solid"
                    icon={<CheckCircleOutlined />}
                    onClick={() => onConfirm(validOrders)}
                    loading={loading}
                    disabled={hasInvalidOrders || validOrders.length === 0}
                >
                    Confirm Shipment ({validOrders.length} items)
                </Button>
            ]}
            width='85%'
            className="confirm-shipment-modal"
            styles={{
                body: {
                    padding: '24px'
                }
            }}
        >
            <div style={{ marginBottom: 16 }}>
                <Text>Are you sure you want to confirm shipment for the following order items?</Text>
                <br />
                <Text type="secondary">This will notify Amazon that these items have been shipped.</Text>
            </div>

            {hasInvalidOrders && (
                <Alert
                    message="Some items cannot be confirmed"
                    description={`${invalidOrders.length} item(s) have validation errors. Requirements: YunExpress/Yanwen carriers require last mile tracking number.`}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Table
                dataSource={allOrders}
                rowKey="id"
                pagination={false}
                size="small"
                columns={columns}
                scroll={{ y: 400 }}
                rowClassName={(record) => {
                    if (record.errorReason) {
                        return 'error-row';
                    }
                    return '';
                }}
            />

            <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6 }}>
                <Text strong style={{ color: '#52c41a' }}>
                    Valid Items: {validOrders.length}
                </Text>
                {hasInvalidOrders && (
                    <>
                        <Text style={{ marginLeft: 16 }} strong type="danger">
                            Invalid Items: {invalidOrders.length}
                        </Text>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ConfirmShipmentModal;
